<template>
  <v-dialog
    v-model="isOpen"
    max-width="600"
    scrollable
    content-class="checkinDialog"
  >
    <loading-plan-dialog ref="loadingPlanDialog" message="Please wait while we create your new plan" />
    <select-debt-dialog 
      ref="selectDebtDialog" 
      :household="household" 
      :ignore-debts="debtIdsForIgnore"
      @on-select="addDebt"
      @on-debt-updated="$emit('on-debt-updated',$event)"
    />
    <select-policy-dialog 
      ref="selectPolicyDialog"
      :household="household"
      :ignore-policies="policyIdsForIgnore"
      @on-select="addPolicy"
      @on-policy-updated="$emit('on-policy-updated',$event)"
    />

    <v-dialog
      v-model="advancedSettingsDialog"
      max-width="400px"
    >
      <v-card>
        <v-card-title>Advanced Plan Settings</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12" class="pr-1">
              <div class="overline mb-1">Additional Loan Principal</div>
              <v-text-field
                v-model="new_additional_loan_principal"
                outlined
                dense
                hide-details
                type="number"
                min="0"
                step="1"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="secondary" @click="advancedSettingsDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card>
      <v-card-title class="pa-0">
        <v-toolbar
          dark
          color="primary"
        >
          <v-toolbar-title>WealthyIQ Check In</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn
              icon
              dark
              @click="close()"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
      </v-card-title>
      <v-card-text class="px-0 pt-2">
        <v-stepper v-model="step" class="elevation-0">
          <v-stepper-header class="elevation-0">
            <v-stepper-step
              :complete="step > 1"
              step="1"
            >
              Liabilities
            </v-stepper-step>

            <v-divider></v-divider>

            <v-stepper-step
              :complete="step > 2"
              step="2"
            >
              Policies
            </v-stepper-step>

            <v-divider></v-divider>

            <v-stepper-step step="3">
              Additional Info
            </v-stepper-step>
          </v-stepper-header>

          <v-stepper-items>
            <v-stepper-content step="1" class="py-0">
              <v-row no-gutters class="checkinDebtsContainer">
                <v-col cols="12" class="text-center title mb-2">
                  Please verify the current debt balances
                </v-col>
                <v-col
                  v-for="debt in new_debts"
                  :key="'checkindebt' + debt.id"
                  cols="12"
                  class="py-2"
                >
                  <v-row no-gutters class="flex-nowrap align-center">
                    <v-col class="subtitle-1">{{ debt.name }}</v-col>
                    <v-col class="checkinInputContainer">
                      <v-text-field
                        v-model="debt.checkin_balance"
                        outlined
                        hide-details
                        dense
                        :placeholder="(debt.checkin_balance_placeholder === null ? 'Unknown' : debt.checkin_balance_placeholder + '')"
                      />
                    </v-col>
                    <v-col cols="1">
                      <v-btn text color="error" v-if="debt.checkin_added" style="min-width: 0px;" @click="removeDebt(debt)"><v-icon small>mdi-minus</v-icon></v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col cols="12" class="text-center my-2">
                  <v-btn small color="primary" class="elevation-0" @click="$refs.selectDebtDialog.open()"><v-icon small>mdi-plus</v-icon></v-btn>
                </v-col>
              </v-row>
            </v-stepper-content>

            <v-stepper-content step="2" class="py-0">
              <v-row no-gutters class="checkinDebtsContainer">
                <v-col cols="12" class="text-center title mb-2">
                  Please confirm policies & loan balances
                </v-col>
                <v-col
                  v-for="policy in new_policies"
                  :key="'checkpolicy' + policy.id"
                  cols="12"
                  class="py-2"
                >
                  <v-row no-gutters class="flex-nowrap align-center">
                    <v-col class="subtitle-1">{{ policy.name }}</v-col>
                    <v-col class="checkinInputContainer">
                      <v-text-field
                        v-model="policy.checkin_loan_balance"
                        outlined
                        hide-details
                        dense
                        :placeholder="(policy.checkin_loan_balance_placeholder === null ? 'Unknown' : policy.checkin_loan_balance_placeholder + '')"
                      />
                    </v-col>
                    <v-col cols="1">
                      <v-btn text color="error" v-if="policy.checkin_added" style="min-width: 0px;" @click="removePolicy(policy)"><v-icon small>mdi-minus</v-icon></v-btn>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row no-gutters>
                <v-col cols="12" class="text-center my-2">
                  <v-btn small color="primary" class="elevation-0" @click="$refs.selectPolicyDialog.open()"><v-icon small>mdi-plus</v-icon></v-btn>
                </v-col>
              </v-row>
            </v-stepper-content>

            <v-stepper-content step="3" class="py-0">
              <v-row no-gutters class="checkinAdditionalContainer">
                <v-col cols="12" class="text-center title mb-2">
                  Please Confirm Available Capital
                </v-col>
                <v-col
                  cols="12"
                  class="py-2"
                >
                  <v-row no-gutters class="flex-nowrap align-center">
                    <v-col class="subtitle-1">Available Monthly Capital</v-col>
                    <v-col class="checkinInputContainer">
                      <v-text-field
                        v-model="new_monthly_capital"
                        outlined
                        hide-details
                        dense
                        :placeholder="debtMonthlyCapital + ''"
                      />
                    </v-col>
                  </v-row>

                  <v-row no-gutters class="flex-nowrap align-center">
                    <v-col class="subtitle-1">Currently Paying</v-col>
                    <v-col class="checkinInputContainer text-right pr-2">
                      {{ currentMonthlyCapital | currencyRounded }}
                    </v-col>
                  </v-row>
                  <v-row no-gutters class="flex-nowrap align-center">
                    <v-col class="subtitle-1">Minimum Required</v-col>
                    <v-col class="checkinInputContainer text-right pr-2">
                      {{ monthlyCapitalMinimum | currencyRounded }}
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-card-text>
      <v-card-actions class="justify-center" style="position: relative;">
        <v-btn text color="secondary" @click="previousStep()" :disabled="step == 1">Back</v-btn>
        <v-btn color="primary" @click="nextStep()" :disabled="!canContinue">{{ nextStepText }}</v-btn>

        <v-btn v-if="showAdvancedButton" @click="advancedSettingsDialog = true" text color="grey" small style="position: absolute; right: 10px; bottom: 10px;">Advanced</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import LoadingPlanDialog from './LoadingPlanDialog'
import SelectDebtDialog from './SelectDebtDialog'
import SelectPolicyDialog from './SelectPolicyDialog'

export default {
  name: 'CheckinDialog',
  props: {
    household: {
      type: Object,
      required: true
    },
    plan: {
      type: Object,
      required: true
    }
  },
  components: {
    LoadingPlanDialog,
    SelectDebtDialog,
    SelectPolicyDialog
  },
  data() {
    return {
      step: 1,
      isOpen: false,
      advancedSettingsDialog: false,
      showAdvancedButton: false,

      newStartMenu: false,
      new_start_date: null,
      new_start_date_formatted: null,

      new_additional_capital: null,
      new_debts: [],
      new_policies: [],

      new_additional_loan_principal: null,

      new_monthly_capital: null
    }
  },
  mounted() {
    document.addEventListener('keydown', this.keyCheck)
    document.addEventListener('keyup', this.keyCheck)
  },
  beforeDestroy() {
    document.removeEventListener('keydown',this.keyCheck)
    document.removeEventListener('keyup',this.keyCheck)
  },
  watch: {
    isOpen(newVal) {
      if(newVal) this.reset()
    }
  },
  computed: {
    nextStepText() {
      return (this.step == 3 ? 'Check In' : 'Next')
    },
    debtsSorted() {
      let debts = JSON.parse(JSON.stringify(this.new_debts))
      debts.sort((a,b) => parseInt(b.balance) - parseInt(a.balance))
      return debts
    },
    debtIdsForIgnore() {
      let arr = []
      this.new_debts.map(d => {
        arr.push(d.id)
        if(d.original_debt_id) arr.push(d.original_debt_id)
      })
      return arr
    },
    finalDebtIds() {
      return this.new_debts.map(d => d.id)
    },
    policiesSorted() {
      return JSON.parse(JSON.stringify(this.new_policies))
    },
    policyIdsForIgnore() {
      let arr = []
      this.policiesSorted.map(p => {
        arr.push(p.id)
        if(p.original_policy_id) arr.push(p.original_policy_id)
      })
      return arr
    },
    finalPolicyIds() {
      return this.policiesSorted.map(p => p.id)
    },
    currentMonth() {
      return this.$moment().format('YYYY-MM')
    },
    canContinue() {
      if(this.step == 1) {
        for(let i = 0; i < this.new_debts.length; i++) {
          let tempplc = this.new_debts[i].checkin_balance_placeholder
          let tempbal = this.new_debts[i].checkin_balance
          if((tempbal == null || tempbal == '') && tempplc !== null) continue
          if(tempbal == null || tempbal == '') return false
          let temp = parseFloat((tempbal + '').replace(/[^0-9.]/,''))
          if(isNaN(temp)) return false
        }
      }
      if(this.step == 2) {
        for(let i = 0; i < this.new_policies.length; i++) {
          let tempplc = this.new_policies[i].checkin_loan_balance_placeholder
          let tempbal = this.new_policies[i].checkin_loan_balance
          if((tempbal == null || tempbal == '') && tempplc !== null) continue
          if(tempbal == null || tempbal == '') return false
          let temp = parseFloat((tempbal + '').replace(/[^0-9.]/,''))
          if(isNaN(temp)) return false
        }
      }
      if(this.step == 3) {
        if(this.new_monthly_capital !== null && this.new_monthly_capital != '') {
          let temp = parseInt(this.new_monthly_capital)
          if(isNaN(temp)) return false
          if(temp < this.monthlyCapitalMinimum) return false
        }
      }
      return true
    },
    debtMonthlyCapital() {
      let capital = 0
      for(let i = 0; i < this.new_debts.length; i++) {
        if(!this.new_debts[i].minimum_payment) continue
        capital += Math.ceil(parseFloat( (this.new_debts[i].minimum_payment + '').replace(/[^0-9.]/,'') ))
      }
      return capital
    },
    currentMonthlyCapital() {
      let capital = 0
      for(let i = 0; i < Object.keys(this.plan.data.debts).length; i++) {
        let key = Object.keys(this.plan.data.debts)[i]
        capital += Math.ceil(parseFloat( (this.plan.data.debts[key].minimum_payment + '').replace(/[^0-9.]/,'') ))
      }
      return capital
    },
    monthlyCapitalMinimum() {
      let capital = 0
      for(let i = 0; i < this.new_debts.length; i++) {
        let tempplc = this.new_debts[i].checkin_balance_placeholder
        let tempbal = this.new_debts[i].checkin_balance
        let amtToUse = 0
        if((tempbal == null || tempbal == '') && tempplc !== null) {
          amtToUse = tempplc
        } else if(tempbal !== null && tempbal != '') {
          amtToUse = tempbal
        }
        let temp = parseFloat((amtToUse + '').replace(/[^0-9.]/,''))
        if(temp > 0) capital += Math.ceil(parseFloat( (this.new_debts[i].minimum_payment + '').replace(/[^0-9.]/,'') ))
      }
      return capital
    }
  },
  methods: {
    open() {
      this.isOpen = true
    },
    close() {
      this.isOpen = false
    },
    reset() {
      this.step                           = 1
      this.advancedSettingsDialog         = false
      this.new_additional_capital         = this.plan.additional_capital || null
      this.new_additional_loan_principal  = this.plan.additional_policy_replenishment
      let debtArr                         = []
      let policyArr                       = []

      for(let i = 0; i < Object.keys(this.plan.data.debts).length; i++) {
        let key = Object.keys(this.plan.data.debts)[i]
        let newDebt = JSON.parse(JSON.stringify(this.plan.data.debts[key]))
        newDebt.checkin_balance = null
        newDebt.checkin_balance_placeholder = null
        if(newDebt.detail) {
          if(this.$moment(newDebt.plan_end_date).format('YYYY-MM') < this.currentMonth ) {
            newDebt.checkin_balance_placeholder = 0
          } else if(newDebt.detail[ this.currentMonth ]) {
            newDebt.checkin_balance_placeholder = Math.round(newDebt.detail[ this.currentMonth ].start_balance)
          }
        }
        debtArr.push( newDebt )
      }
      this.new_debts                = debtArr

      for(let i = 0; i < Object.keys(this.plan.data.policies).length; i++) {
        let key = Object.keys(this.plan.data.policies)[i]
        let newPolicy = JSON.parse(JSON.stringify(this.plan.data.policies[key]))
        newPolicy.checkin_loan_balance = null
        newPolicy.checkin_loan_balance_placeholder = 0
        if(newPolicy.detail && newPolicy.detail[ this.currentMonth ]) newPolicy.checkin_loan_balance_placeholder = Math.round(newPolicy.detail[ this.currentMonth ].loan_balance_start)
        policyArr.push( newPolicy )
      }
      this.new_policies             = policyArr

      if(this.new_additional_capital) this.new_monthly_capital = this.debtMonthlyCapital + this.new_additional_capital
    },
    nextStep() {
      if(this.canContinue) {
        if(this.step == 3) {
          this.save();
        } else {
          this.step++;
        }
      }
    },
    previousStep() {
      this.step--
    },
    save() {
      this.$refs.loadingPlanDialog.open()

      //Debts should be an array of objects each containing ID and Balance of a debt
      //Policies should be an array of objects each containing ID and Loan Balance of a policy
      let finalDebts    = []
      let finalPolicies = []
      this.new_debts.forEach(d => {
        let tempplc = d.checkin_balance_placeholder
        let tempbal = d.checkin_balance
        if((tempbal == null || tempbal == '') && tempplc !== null) {
          finalDebts.push({id: d.id, balance: tempplc})
        } else {
          finalDebts.push({id: d.id, balance: tempbal})
        }
      })
      this.new_policies.forEach(p => {
        let tempplc = p.checkin_loan_balance_placeholder
        let tempbal = p.checkin_loan_balance
        if((tempbal == null || tempbal == '') && tempplc !== null) {
          finalPolicies.push({id: p.id, loan_balance: tempplc})
        } else {
          finalPolicies.push({id: p.id, loan_balance: tempbal})
        }
      })

      let data = {
        debts:              finalDebts,
        policies:           finalPolicies,
        available_capital:  this.new_monthly_capital || this.debtMonthlyCapital
      }
      this.$http.post('household-plans/' + this.plan.id + '/checkin', data)
        .then(response => {
          this.$emit('on-plan-updated', response.data)
          this.close()
        })
        .catch(() => {
          this.$swal.fire('Error', 'There was an error updating the plan. Please try again.', 'error')
        })
        .finally(() => {
          this.$refs.loadingPlanDialog.close()
        })
    },
    removeDebt(debt) {
      this.new_debts = this.new_debts.filter(d => d.id != debt.id)
    },
    removePolicy(policy) {
      this.new_policies = this.new_policies.filter(p => p.id != policy.id)
    },
    addDebt(debt) {
      let newDebt = JSON.parse(JSON.stringify(debt))
      let isCurrMonth = (this.currentMonth == this.$moment(newDebt.balance_as_of).format('YYYY-MM'))
      let theoreticalBalance = null
      if(isCurrMonth) theoreticalBalance = Math.round(newDebt.balance)
      if(!isCurrMonth && newDebt.amortizationSchedule && newDebt.amortizationSchedule[ this.currentMonth ]) theoreticalBalance = Math.round(newDebt.amortizationSchedule[ this.currentMonth ].start_balance)
      newDebt.checkin_balance = theoreticalBalance
      newDebt.checkin_balance_placeholder = theoreticalBalance 
      newDebt.checkin_added   = 1
      this.new_debts.push(newDebt)
    },
    addPolicy(policy) {
      let newPolicy = JSON.parse(JSON.stringify(policy))
      newPolicy.checkin_loan_balance = 0
      newPolicy.checkin_loan_balance_placeholder = 0 
      newPolicy.checkin_added   = 1
      this.new_policies.push(newPolicy)
    },

    keyCheck (e) {
      if( (e.shiftKey && e.metaKey) || (e.shiftKey && e.ctrlKey) ) {
        this.showAdvancedButton = true
      } else {
        this.showAdvancedButton = false
      }
    },
  }
}
</script>

<style lang="scss" scoped>
  .checkinDebtsContainer {
    >div {
      border-bottom: thin solid rgba(0,0,0,.1);
      &:first-child,
      &:last-child {
        border-bottom-color: transparent;
      }
    }
  }
  .checkinInputContainer {
    max-width: 120px;
    min-width: 120px;
    width: 120px;
  }
</style>